import React from "react";
import {
  Page,
  Text,
  Document,
  StyleSheet,
  Image,
  View,
  Font,
} from "@react-pdf/renderer";
import clap_service_logo from "../../assets/images/clap-service-logo.png";
import { AccountType, Currency } from "../../shared/constant";
import clapUtils, { isClapAdminORClapManager } from "../../utils/clapUtils";
import dateUtils from "../../utils/dateUtils";

Font.register({
  family: "Noto Sans Bengali",
  // eslint-disable-next-line global-require
  src: require("../../assets/fonts/NotoSansBengali-Regular.ttf"),
});

const styles = StyleSheet.create({
  body: {
    fontFamily: "Noto Sans Bengali",
    paddingTop: 35,
    paddingBottom: 65,
    paddingHorizontal: 35,
    fontSize: 14,
  },
  card: {
    backgroundColor: "#EFEFEF",
    padding: 12,
    borderRadius: 2,
  },
  header: {
    fontSize: 12,
    marginBottom: 30,
    textAlign: "center",
    color: "grey",
  },
  image: {
    marginHorizontal: 200,
    width: 120,
    height: 120,
  },
  title: {
    fontSize: 20,
    textAlign: "center",
  },
  fontSize14: {
    fontSize: 14,
  },
  text: {
    fontSize: 13,
  },
  fontSize12: {
    fontSize: 12,
  },
  fontSize11: {
    fontSize: 11,
  },
  author: {
    fontSize: 12,
    textAlign: "center",
  },
  pageNumber: {
    position: "absolute",
    fontSize: 12,
    bottom: 30,
    left: 0,
    right: 0,
    textAlign: "center",
    color: "grey",
  },

  metalicOrange: {
    color: "#EB6711",
  },
  textSecondary: {
    color: "rgba(0,0,0,.45)",
  },
});

const getIncomeShare = (shares: any, clientTypes: any) => {
  if (!shares && !clientTypes) return <Text>{}</Text>;
  let types = clientTypes?.length && clientTypes.map((item: any) => item.label);
  if (types[0] === AccountType.MEMBER)
    types = [
      AccountType.LABEL,
      AccountType.ARTIST,
      AccountType.LYRICIST,
      AccountType.COMPOSER,
    ];
  return (
    <Text>
      {Object.keys(shares).map(
        (item: any) =>
          types.includes(item) && (
            <Text key={item}>
              {
                (() => {
                  if (clientTypes[0].label === AccountType.MEMBER) {
                    if (item === AccountType.LABEL && !shares[item])
                      return <Text>{}</Text>;
                    return (
                      <Text>{`${
                        item === AccountType.LABEL ? "Producer" : item
                      } (${shares[item] || 0}%)  `}</Text>
                    );
                  }
                  return <Text>{`Producer (${shares[item] || 0}%)`}</Text>;
                })()

                // clientTypes[0].label === AccountType.MEMBER
                //   ? `${item} (${shares[item] || 0}%)`
                //   : `${shares[item] || 0}%`
              }
            </Text>
          ),
      )}
    </Text>
  );
};

const isAnyFilterExists = (filterFields: any) => {
  if (
    (filterFields.labels && filterFields.labels.length) ||
    (filterFields.artist && filterFields.artist.length) ||
    (filterFields.lyricist && filterFields.lyricist.length) ||
    (filterFields.composer && filterFields.composer.length) ||
    (filterFields.track && filterFields.track.length) ||
    (filterFields.platform && filterFields.platform.length) ||
    (filterFields.distributor && filterFields.distributor.length)
  )
    return true;
  return false;
};

// const getAccountTypeColor = (type: string) => {
//   switch (type) {
//     case AccountType.LABEL:
//       return { color: "#1890FF" };
//     case AccountType.MEMBER:
//       return { color: "#531DAB" };

//     default:
//       return { color: "" };
//   }
// };

const PDFRenderedDoc = ({
  isClientDetails,
  userDetails,
  client,
  tracksIncomeSummary,
  filterFields,
  apiData,
  currency,
  dateRange,
  clientId,
}: {
  isClientDetails: Boolean;
  userDetails: any;
  client: any;
  tracksIncomeSummary: any;
  filterFields: any;
  apiData: any;
  currency: any;
  dateRange: any;
  clientId: any;
}) => {
  /* REUSE: can reuse these functions from ClapIncomes */
  const getFirstTotal = {
    income: function income(c: string) {
      switch (c) {
        case Currency.USD:
          if (isClientDetails)
            return tracksIncomeSummary?.client_total_income_usd || 0;
          if (isClapAdminORClapManager(userDetails))
            return tracksIncomeSummary?.clap_total_income_usd || 0;
          return tracksIncomeSummary?.client_total_income_usd || 0;
        case Currency.EUR:
          if (isClientDetails)
            return tracksIncomeSummary?.client_total_income_eur || 0;
          if (isClapAdminORClapManager(userDetails))
            return tracksIncomeSummary?.clap_total_income_eur || 0;
          return tracksIncomeSummary?.client_total_income_eur || 0;
        case Currency.BDT:
          if (isClientDetails)
            return tracksIncomeSummary?.client_total_income_bdt || 0;
          if (isClapAdminORClapManager(userDetails))
            return tracksIncomeSummary?.clap_total_income_bdt || 0;
          return tracksIncomeSummary?.client_total_income_bdt || 0;

        default:
          return 0;
      }
    },
    payable: function payable(c: string) {
      switch (c) {
        case Currency.USD:
          return tracksIncomeSummary?.total_payable_usd || 0;
        case Currency.EUR:
          return tracksIncomeSummary?.total_payable_eur || 0;

        case Currency.BDT:
          return tracksIncomeSummary?.total_payable_bdt || 0;

        default:
          return 0;
      }
    },
    labelReceived: function labelReceived(c: string) {
      switch (c) {
        case Currency.USD:
          return tracksIncomeSummary?.track_total_net_income_usd || 0;
        case Currency.EUR:
          return tracksIncomeSummary?.track_total_net_income_eur || 0;

        case Currency.BDT:
          return tracksIncomeSummary?.track_total_net_income_bdt || 0;

        default:
          return 0;
      }
    },
  };

  const getSecondTotal = (c: string) => {
    switch (c) {
      case Currency.USD:
        if (isClientDetails) return getFirstTotal.payable(Currency.USD);
        if (isClapAdminORClapManager(userDetails))
          return getFirstTotal.labelReceived(Currency.USD);
        return getFirstTotal.payable(Currency.USD);
      case Currency.EUR:
        if (isClientDetails) return getFirstTotal.payable(Currency.EUR);
        if (isClapAdminORClapManager(userDetails))
          return getFirstTotal.labelReceived(Currency.EUR);
        return getFirstTotal.payable(Currency.EUR);
      case Currency.BDT:
        if (isClientDetails) return getFirstTotal.payable(Currency.BDT);
        if (isClapAdminORClapManager(userDetails))
          return getFirstTotal.labelReceived(Currency.BDT);
        return getFirstTotal.payable(Currency.BDT);
      default:
        return 0;
    }
  };
  const getSecondTotalTitle = () => {
    if (isClientDetails) return "Total Payable";
    if (isClapAdminORClapManager(userDetails)) return "Label Received";
    return "Total Receivable";
  };

  return (
    <Document>
      <Page style={styles.body} size="A4">
        <View style={styles.header} fixed>
          <Image style={styles.image} src={clap_service_logo} />
          <Text>~ info@clap.services ~</Text>
        </View>

        <View style={{ marginBottom: clientId ? 20 : 22 }}>
          <Text style={{ marginBottom: 4 }}>
            <Text style={styles.title}>
              {clientId ? client?.name : "Gross Report"}{" "}
            </Text>
            {/* {clientId && (
              <Text
                style={[
                  styles.author,
                  getAccountTypeColor(client?.client_types[0].label),
                ]}
              >
                {client?.client_types[0].label}
              </Text>
            )} */}
          </Text>
          <Text style={styles.author}>
            {dateRange.from && (
              <Text>
                Period:{" "}
                {`${
                  dateRange.from ? `${dateRange.from} ~ ${dateRange.to}` : ""
                } ${currency ? "|" : ""} `}
              </Text>
            )}
            {currency && <Text>Currency: {currency}</Text>}
          </Text>
        </View>

        {clientId && (
          <View style={[styles.fontSize12, { marginBottom: 22 }]}>
            {client?.is_pdf_income_share_visible && (
              <Text style={{ paddingBottom: 2 }}>
                <Text>Income Share:{"  "}</Text>
                <Text>
                  {getIncomeShare(
                    client?.display_income_share,
                    client?.client_types,
                  )}
                </Text>
              </Text>
            )}
            {client?.is_pdf_clap_charge_visible && (
              <Text style={{ paddingBottom: 2 }}>
                <Text>CLAP charges: </Text>
                <Text>
                  {client?.display_clap_service_charge.clap_service_charge}%
                </Text>
              </Text>
            )}
            {isClapAdminORClapManager(userDetails) && client?.last_payment && (
              <Text>
                <Text>Last Payment:{"  "}</Text>
                {dateUtils.defaultFormat(client.last_payment)}
              </Text>
            )}
          </View>
        )}

        <Text style={[styles.text, { marginBottom: 4 }]}>Filters Used</Text>
        <View style={[styles.card, styles.fontSize12, { marginBottom: 16 }]}>
          {!isAnyFilterExists(filterFields) ? (
            <Text style={styles.textSecondary}>None</Text>
          ) : (
            <>
              {filterFields.labels && filterFields.labels.length && (
                <Text style={{ paddingBottom: 4 }}>
                  <Text>Label:{"  "}</Text>
                  {clapUtils.getNameFromFilter(
                    apiData?.allLabels,
                    filterFields.labels,
                    "Label",
                  )}
                </Text>
              )}
              {filterFields.artist && filterFields.artist.length && (
                <Text style={{ paddingBottom: 4 }}>
                  <Text>Artist:{"  "}</Text>
                  {clapUtils.getNameFromFilter(
                    apiData?.allArtists,
                    filterFields.artist,
                    "Artist",
                  )}
                </Text>
              )}
              {filterFields.lyricist && filterFields.lyricist.length && (
                <Text style={{ paddingBottom: 4 }}>
                  <Text>Lyricist:{"  "}</Text>
                  {clapUtils.getNameFromFilter(
                    apiData?.allLyricists,
                    filterFields.lyricist,
                    "Lyricist",
                  )}
                </Text>
              )}
              {filterFields.composer && filterFields.composer.length && (
                <Text style={{ paddingBottom: 4 }}>
                  <Text>Composer:{"  "}</Text>
                  {clapUtils.getNameFromFilter(
                    apiData?.allComposers,
                    filterFields.composer,
                    "Composer",
                  )}
                </Text>
              )}
              {filterFields.track && filterFields.track.length && (
                <Text style={{ paddingBottom: 4 }}>
                  <Text>Track:{"  "}</Text>
                  {clapUtils.getNameFromFilter(
                    apiData?.allTracks,
                    filterFields.track,
                    "Track",
                  )}
                </Text>
              )}
              {filterFields.platform && filterFields.platform.length && (
                <Text>
                  <Text>Platform:{"  "}</Text>
                  {clapUtils.getNameFromFilter(
                    apiData?.allPlatforms,
                    filterFields.platform,
                    "Platform",
                  )}
                </Text>
              )}
              {filterFields.distributor && filterFields.distributor.length && (
                <Text>
                  <Text>Distributor:{"  "}</Text>
                  {clapUtils.getNameFromFilter(
                    apiData?.allDistributors,
                    filterFields.distributor,
                    "Distributor",
                  )}
                </Text>
              )}
            </>
          )}
        </View>

        <Text style={[styles.text, { marginBottom: 4 }]}>Total Income</Text>
        <View style={[styles.card, styles.text, { marginBottom: 16 }]}>
          <Text>
            <Text style={styles.fontSize11}>USD{"  "}</Text>
            <Text style={styles.metalicOrange}>
              {getFirstTotal.income(Currency.USD)
                ? parseFloat(getFirstTotal.income(Currency.USD)).toFixed(3)
                : 0}
            </Text>
          </Text>
          <Text style={{ paddingVertical: 3 }}>
            <Text style={styles.fontSize11}>EUR{"  "}</Text>
            <Text style={styles.metalicOrange}>
              {getFirstTotal.income(Currency.EUR)
                ? parseFloat(getFirstTotal.income(Currency.EUR)).toFixed(3)
                : 0}
            </Text>
          </Text>
          <Text>
            <Text style={styles.fontSize11}>BDT{"  "}</Text>
            <Text style={styles.metalicOrange}>
              {getFirstTotal.income(Currency.BDT)
                ? parseFloat(getFirstTotal.income(Currency.BDT)).toFixed(3)
                : 0}
            </Text>
          </Text>
        </View>

        <Text style={[styles.text, { marginBottom: 4 }]}>
          {getSecondTotalTitle()}
        </Text>
        <View style={[styles.card, styles.text]}>
          <Text>
            <Text style={styles.fontSize11}>USD{"  "}</Text>
            <Text style={styles.metalicOrange}>
              {getSecondTotal(Currency.USD)
                ? parseFloat(getSecondTotal(Currency.USD)).toFixed(3)
                : 0}
            </Text>
          </Text>
          <Text style={{ paddingVertical: 3 }}>
            <Text style={styles.fontSize11}>EUR{"  "}</Text>
            <Text style={styles.metalicOrange}>
              {getSecondTotal(Currency.EUR)
                ? parseFloat(getSecondTotal(Currency.EUR)).toFixed(3)
                : 0}
            </Text>
          </Text>
          <Text>
            <Text style={styles.fontSize11}>BDT{"  "}</Text>
            <Text style={styles.metalicOrange}>
              {getSecondTotal(Currency.BDT)
                ? parseFloat(getSecondTotal(Currency.BDT)).toFixed(3)
                : 0}
            </Text>
          </Text>
        </View>

        <Text
          style={styles.pageNumber}
          render={({ pageNumber, totalPages }) =>
            `${pageNumber} / ${totalPages}`
          }
          fixed
        />
      </Page>
    </Document>
  );
};

export default PDFRenderedDoc;
